import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import PrimaryButton from 'components/common/buttons/PrimaryButton'
import BaseModal from 'components/common/modals/BaseModal'
import ProfilePicture from 'components/common/ProfilePicture'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { ROUTES } from 'consts'
import moment from 'moment'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { trackEvent } from 'utils/analyticsUtils'
import { createIdForTest } from 'utils/testingUtils.js'

const useStyles = makeStyles(() => ({
  modal: {
    minWidth: 700
  },
  columnTitle: {
    marginBottom: 24
  },
  divider: {
    borderLeft: '1px solid var(--border-color-11)'
  },
  suggestionsColumn: {
    // Breathing room before the divider, mirroring createColumn's paddingLeft so the divider sits
    // optically centred. Measured on the rendered modal: a 20px gap to the divider. Only applied
    // when there IS a divider.
    paddingRight: 40
  },
  createColumn: {
    paddingLeft: 40
  },
  suggestion: {
    cursor: 'pointer',
    padding: '8px 12px',
    // The row is a `Grid container spacing={2}`, which MUI gives `width: calc(100% + 16px)` and a
    // negative margin. Because both a width and a left margin are set, the right margin is
    // over-constrained and ignored (CSS 2.1 10.3.3) - so a `marginRight: -12` here did nothing,
    // and the row still overhung its column by the leftover `16 - 12 = 4px`, crossing the divider.
    // Setting the width explicitly makes the bleed a symmetric 12px that does not depend on the
    // gutter: change `spacing` above and this stays correct.
    width: 'calc(100% + 24px)',
    marginLeft: -12,
    borderRadius: 8,
    '&:hover, &:focus-visible': {
      background: 'var(--bg-color-14)'
    }
  },
  suggestionMeta: {
    color: 'var(--text-color-11)'
  }
}))

const DISPLAY_DATE_FORMAT = 'M/D/YYYY'

/**
 * Birthdates are stored as AWSDateTime but carry date-only meaning, so read them in UTC —
 * otherwise a midnight-UTC value renders as the previous day for viewers west of it.
 */
const formatBirthdate = birthdate => {
  const parsed = moment.utc(birthdate)

  return birthdate && parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : null
}

/**
 * With exactly one exact match we are here because uniqueness could not be proven from a truncated
 * page — an exact match *was* found, so "we couldn't find an exact match" would be wrong.
 */
export const createOrAddTitleKey = exactMatchCount => {
  if (exactMatchCount > 1) {
    return 'pms.createOrAdd.multipleMatchesTitle'
  }

  return exactMatchCount === 1 ? 'pms.createOrAdd.unconfirmedMatchTitle' : 'pms.createOrAdd.title'
}

const PmsCreateOrAddPatientModal = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const { isOpen, patientName, suggestions, exactMatchCount, createInitialValues } = useSelector(
    state => state.pmsReducer.createOrAddPopup
  )

  const handleClose = useCallback(() => {
    dispatch(Actions.pmsToggleCreateOrAddModal({ isOpen: false }))
  }, [dispatch])

  const handleSelectPatient = useCallback(
    patient => {
      // Deliberately no patient name here — these events land in Mixpanel unredacted.
      trackEvent('PMS Create or Add - patient selected', {
        suggestionCount: suggestions.length
      })
      history.push(`${ROUTES.PATIENTS}/${patient.id}`)
      handleClose()
    },
    [handleClose, history, suggestions.length]
  )

  const handleCreatePatient = useCallback(() => {
    trackEvent('PMS Create or Add - create clicked', {
      suggestionCount: suggestions.length
    })
    dispatch(
      Actions.setCreateRecordsPatientModalOpen({
        isOpen: true,
        initialValues: createInitialValues
      })
    )
    handleClose()
  }, [createInitialValues, dispatch, handleClose, suggestions.length])

  return (
    isOpen && (
      <BaseModal
        id="pms-create-or-add-modal"
        closeButtonId="pms-create-or-add-close"
        open={isOpen}
        withCloseIcon
        variant="alert"
        title={t(createOrAddTitleKey(exactMatchCount), { patientName })}
        handleClose={handleClose}
        className={classes.modal}
      >
        <Grid container>
          <Grid
            item
            xs={createInitialValues ? 7 : 12}
            className={createInitialValues ? classes.suggestionsColumn : undefined}
          >
            <DazzedParagraph14 strong className={classes.columnTitle}>
              {t('pms.createOrAdd.openExistingPatient')}
            </DazzedParagraph14>
            <Grid container direction="column" spacing={2}>
              {suggestions.map(patient => {
                const birthdate = formatBirthdate(patient.birthdate)
                // Leads missing a name part are stored with `name: undefined`.
                const displayName = patient.name || [patient.firstName, patient.lastName].filter(Boolean).join(' ')

                return (
                  <Grid item key={patient.id}>
                    <Grid
                      id={createIdForTest({ componentId: 'pms-create-or-add-suggestion', title: patient.id })}
                      container
                      alignItems="center"
                      spacing={2}
                      wrap="nowrap"
                      className={classes.suggestion}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectPatient(patient)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleSelectPatient(patient)
                        }
                      }}
                    >
                      <Grid item>
                        <ProfilePicture name={displayName} size="medium" />
                      </Grid>
                      <Grid item>
                        <DazzedParagraph14 strong>
                          {birthdate ? `${displayName} | ${birthdate}` : displayName}
                        </DazzedParagraph14>
                        {patient.createdAt && (
                          <DazzedParagraph12 className={classes.suggestionMeta}>
                            {t('pms.createOrAdd.createdAt', {
                              fromNow: moment(patient.createdAt).fromNow(),
                              date: moment(patient.createdAt).format(DISPLAY_DATE_FORMAT)
                            })}
                          </DazzedParagraph12>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
          {createInitialValues && (
            <Grid item xs={5} className={`${classes.divider} ${classes.createColumn}`}>
              <DazzedParagraph14 strong className={classes.columnTitle}>
                {t('pms.createOrAdd.createNewPatient')}
              </DazzedParagraph14>
              <PrimaryButton
                label={t('pms.createOrAdd.createButton')}
                onClick={handleCreatePatient}
                id="pms-create-or-add-create-btn"
              />
            </Grid>
          )}
        </Grid>
      </BaseModal>
    )
  )
}

export default PmsCreateOrAddPatientModal
